import UseContext from 'hooks/useContext';
import { useCallback, useEffect, useRef } from 'react';

// Something changed in a panel or a form, and the lists that show it reload:
// the change travels in `modalDataReturn` as `{ type, at }`, as the invoice
// reminders do (useInvoiceReminderChanged).
//
// `modalDataReturn` holds one value: two signals sent in the same render
// (record, then companies) would leave only the last one. Each signal also
// carries the recent changes, numbered, so a listener answers every change
// sent since it last looked, not only the last one.

const KEPT_CHANGES = 20;
let lastChangeNumber = 0;
let recentChanges = [];

const changesOf = (signal) => (Array.isArray(signal?.changes) ? signal.changes : null);
const lastNumberOf = (signal) => changesOf(signal)?.reduce((last, change) => Math.max(last, change.number), 0) ?? 0;

export const useNotifyChange = (type) => {
  const { setModalDataReturn } = UseContext();
  return useCallback(() => {
    lastChangeNumber += 1;
    recentChanges = [...recentChanges, { type, number: lastChangeNumber }].slice(-KEPT_CHANGES);
    setModalDataReturn({ type, at: Date.now(), changes: recentChanges });
  }, [setModalDataReturn, type]);
};

export const useOnChange = (type, onChanged) => {
  const { modalDataReturn } = UseContext();
  const lastSeen = useRef(modalDataReturn);
  // Start from every change already made, so that a listener mounted after a
  // plain value does not answer the older changes a later signal still carries.
  const lastSeenNumber = useRef(Math.max(lastNumberOf(modalDataReturn), lastChangeNumber));
  const callback = useRef(onChanged);
  callback.current = onChanged;

  useEffect(() => {
    if (lastSeen.current === modalDataReturn) return;
    lastSeen.current = modalDataReturn;
    const changes = changesOf(modalDataReturn);
    // A value set directly (`{ type: 'employee_created', user }`) has no list.
    if (!changes) {
      if (modalDataReturn?.type === type) callback.current();
      return;
    }
    const unseen = changes.filter((change) => change.number > lastSeenNumber.current);
    lastSeenNumber.current = Math.max(lastSeenNumber.current, lastNumberOf(modalDataReturn));
    if (unseen.some((change) => change.type === type)) callback.current();
  }, [modalDataReturn, type]);
};
